import { useEffect, useRef, useCallback, useMemo } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { useGetUser } from "../api/queries/useGetUser";
import { useAppContext } from "../Context/AppContext";
import { Spinner } from "react-bootstrap";
import { useSaveUser } from "../api/mutations/useSaveUser";
import { isLoggedIn } from "../auth/authStorage";
import { isUnpaidExplorerUser } from "../explorer/isUnpaidExplorerUser";
import { isPaidPortalMember } from "../utils/membershipTier";
import { shouldResumeEarlyStageRegistration } from "../utils/earlyStageRegistrationGate";
import CompleteProfileBanner from "./common/CompleteProfileBanner";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ALLOWED_PATHS = [
  "/register-user",
  "/membership-inactive",
  "/manage-membership",
  "/renew-membership",
  "/event-booking",
  "/event-questions",
  "/confirm-booking",
  "/explorer",
];

function ProtectedRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = isLoggedIn();
  const location = useLocation();
  const { objectId, setUser } = useAppContext();
  const navigate = useNavigate();
  const hasUpdatedRef = useRef(false);

  const handleUpdateSuccess = useCallback(() => {
    if (objectId) {
      localStorage.setItem(`lastLoginUpdated_${objectId}`, "true");
    }
  }, [objectId]);

  const { mutate: updateUser } = useSaveUser({
    onSuccess: handleUpdateSuccess,
  });

  const { data: userData, isLoading, isError, error } = useGetUser({
    id: objectId || "",
    enabled: !!objectId,
  });

  const isUnpaidExplorer = useMemo(
    () => isUnpaidExplorerUser(userData?.user),
    [userData?.user]
  );

  const hasPaidMembership = useMemo(
    () => isPaidPortalMember(userData?.user),
    [userData?.user]
  );

  const isExplorerHomePath = useMemo(() => {
    const p = location.pathname;
    return (
      p === "/dashboard" ||
      p.startsWith("/event-booking") ||
      p.startsWith("/event-questions") ||
      p.startsWith("/confirm-booking") ||
      p.startsWith("/members-benefits-smbf")
    );
  }, [location.pathname]);

  const isExplorerMessagesPath = useMemo(
    () => location.pathname.startsWith("/messages"),
    [location.pathname]
  );

  const isExplorerConnectionsPath = useMemo(
    () => location.pathname.startsWith("/connections"),
    [location.pathname]
  );

  const isExplorerAllowedPath =
    isExplorerHomePath || isExplorerMessagesPath || isExplorerConnectionsPath;

  const isExplorerCheckoutPath = useMemo(
    () =>
      location.pathname === "/explorer/checkout" ||
      location.pathname.startsWith("/explorer/checkout/"),
    [location.pathname]
  );

  useEffect(() => {
    if (userData) {
      setUser(userData);
    }
  }, [userData, setUser]);

  useEffect(() => {
    if (hasUpdatedRef.current || !userData?.user?.id || !objectId) {
      return;
    }

    const storageKey = `lastLoginUpdated_${objectId}`;
    const alreadyUpdated = localStorage.getItem(storageKey);

    if (!alreadyUpdated) {
      hasUpdatedRef.current = true;

      const formData = new FormData();
      const now = new Date().toISOString();

      const updatedData = {
        ...userData.user,
        lastLoggedIn: now,
      };

      Object.entries(updatedData).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          if (typeof value === "object") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, String(value));
          }
        }
      });

      updateUser(formData);
    }
  }, [objectId, updateUser, userData]);

  const isOnAllowedPath = useMemo(() => {
    return ALLOWED_PATHS.some(
      (path) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  }, [location.pathname]);

  const isCancelledMembership = useMemo(() => {
    const stripeStatus = userData?.user?.stripeStatus?.toLowerCase?.();
    return stripeStatus === "canceled" || stripeStatus === "cancelled";
  }, [userData?.user?.stripeStatus]);

  const shouldForceEarlyStageResume = useMemo(() => {
    return shouldResumeEarlyStageRegistration(null, userData?.user);
  }, [userData?.user]);

  const isMissingUserProfile = useMemo(() => {
    const msg =
      (error as any)?.response?.data?.message ||
      (error as any)?.response?.data ||
      (error as any)?.message;
    return (
      isError &&
      (msg === "User does not exist" ||
        (typeof msg === "string" && msg.includes("User does not exist")))
    );
  }, [error, isError]);

  useEffect(() => {
    if (isLoading || !isAuthenticated || isOnAllowedPath) {
      return;
    }

    if (isMissingUserProfile) {
      navigate("/register-user", { replace: true });
      return;
    }

    if (!userData) return;

    if (shouldForceEarlyStageResume && location.pathname !== "/register-user") {
      if (!(isUnpaidExplorer && isExplorerAllowedPath)) {
        navigate("/register-user", { replace: true });
        return;
      }
    }

    if (isExplorerCheckoutPath) {
      if (isUnpaidExplorer) {
        navigate("/event-booking", { replace: true });
      }
      return;
    }

    if (location.pathname === "/dashboard" && isUnpaidExplorer) {
      navigate("/members-benefits-smbf", { replace: true });
      return;
    }

    if (isCancelledMembership) {
      navigate("/membership-inactive", { replace: true });
      return;
    }

    if (!hasPaidMembership) {
      if (isUnpaidExplorer && isExplorerAllowedPath) {
        return;
      }
      navigate(isUnpaidExplorer ? "/event-booking" : "/register-user", {
        replace: true,
      });
    }
  }, [
    isAuthenticated,
    isLoading,
    userData,
    isOnAllowedPath,
    isCancelledMembership,
    isMissingUserProfile,
    isUnpaidExplorer,
    hasPaidMembership,
    shouldForceEarlyStageResume,
    isExplorerAllowedPath,
    location.pathname,
    isExplorerCheckoutPath,
    navigate,
  ]);

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (isLoading && !userData) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );
  }

  if (isMissingUserProfile && !isOnAllowedPath) {
    return (
      <div
        className="d-flex justify-content-center align-items-center vh-100"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <CompleteProfileBanner />
      {children}
    </>
  );
}

ProtectedRoute.displayName = "ProtectedRoute";

export default ProtectedRoute;
